// ====================================================================
// File: src/main.ts  (versión hash-router, sin React)
// - Alineado con subruta /go-planilla
// - Coordinado con TOKEN_KEY de Axios (src/api/index.ts)
// - Más robusto en resolución de BACKEND_URL / API_BASE
// ====================================================================
import './index.css'
import { verificarConexion } from './PingCheck'
import { TOKEN_KEY } from '@/api/index'

// 1) Resolver BASE del backend
const fromEnv =
  (import.meta as any)?.env?.VITE_API_BASE_URL ||
  (import.meta as any)?.env?.VITE_BACKEND_URL ||
  ''

// Si no hay env, por defecto usamos la subruta "/go-planilla"
export const BASE = (fromEnv ? String(fromEnv) : '/go-planilla').replace(/\/+$/, '')
export const API_BASE = `${BASE}/api`

// Exponer en window para otros módulos legacy que lean __CONFIG__
;(window as any).__CONFIG__ = { ...(window as any).__CONFIG__, BACKEND_URL: BASE, API_BASE }

// 2) Router por hash
export type Ruta = 'login' | 'forgot' | 'reset' | 'dashboard' | ''
let rutaActual: Ruta = ''

function parseHash(): { ruta: Ruta; params: URLSearchParams } {
  const raw = (location.hash || '').replace(/^#/, '').trim()
  const [pathPart = '', query = ''] = raw.split('?')
  const path = pathPart.toLowerCase()
  const ruta: Ruta = (['login', 'forgot', 'reset', 'dashboard'].includes(path) ? (path as Ruta) : '')
  return { ruta, params: new URLSearchParams(query) }
}

export function mostrarLogin() {
  const { ruta } = parseHash()
  if (ruta !== 'login') {
    location.hash = 'login'
  } else {
    navegar('login', new URLSearchParams())
  }
}

async function navegar(ruta: Ruta, params: URLSearchParams = new URLSearchParams()) {
  if (ruta === rutaActual) return
  rutaActual = ruta

  const tieneToken = !!localStorage.getItem(TOKEN_KEY)

  try {
    switch (ruta) {
      case 'login': {
        const m = await import('./auth')
        m.mostrarLogin?.()
        break
      }
      case 'forgot': {
        const m = await import('./auth')
        m.mostrarForgotContrasena?.()
        break
      }
      case 'reset': {
        const m = await import('./auth')
        const token = params.get('token') || ''
        m.mostrarResetContrasena?.(token)
        break
      }
      case 'dashboard': {
        if (tieneToken) {
          const m = await import('./dashboard')
          // pasar API_BASE opcionalmente si tu dashboard lo necesita
          m.cargarDashboard?.({ apiBase: API_BASE })
        } else {
          const m = await import('./auth')
          m.mostrarLogin?.()
          rutaActual = 'login'
          if (location.hash !== '#login') location.hash = 'login'
        }
        break
      }
      default: {
        if (tieneToken) {
          const m = await import('./dashboard')
          m.cargarDashboard?.({ apiBase: API_BASE })
          rutaActual = 'dashboard'
          if (location.hash !== '#dashboard') location.hash = 'dashboard'
        } else {
          const m = await import('./auth')
          m.mostrarLogin?.()
          rutaActual = 'login'
          if (location.hash !== '#login') location.hash = 'login'
        }
        break
      }
    }
  } catch (err) {
    console.error('Error cargando ruta:', ruta, err)
    const m = await import('./auth')
    m.mostrarLogin?.()
    rutaActual = 'login'
    if (location.hash !== '#login') location.hash = 'login'
  }
}

// Debounce de cambios de hash
let t: number | undefined
window.addEventListener('hashchange', () => {
  if (t) window.clearTimeout(t)
  t = window.setTimeout(() => {
    const { ruta, params } = parseHash()
    navegar(ruta, params)
  }, 0)
})

// Bootstrap
document.addEventListener('DOMContentLoaded', async () => {
  try {
    // Si tu PingCheck permite pasar base, úsalo; si no, deja como estaba
    // @ts-ignore opcional
    await verificarConexion?.(API_BASE)
  } catch {}
  const { ruta, params } = parseHash()
  navegar(ruta, params)
})

// ====================================================================
// OPCIONAL — Si migras a React Router en /go-planilla (nuestro setup recomendado):
// Reemplaza este archivo por src/main.tsx y monta el <AppRouter />
// ====================================================================
// File: src/main.tsx  (para React + React Router)
// --------------------------------------------------------------------
// import React from 'react'
// import ReactDOM from 'react-dom/client'
// import './index.css'
// import AppRouter from './router'   // el que dejamos con basename "/go-planilla"
// import { migrateLegacyTokenKey, isLoggedIn, me } from '@/api/auth'
//
// migrateLegacyTokenKey()
// if (isLoggedIn()) me().catch(() => {/* opcional: ignorar error */})
//
// ReactDOM.createRoot(document.getElementById('root')!).render(
//   <React.StrictMode>
//     <AppRouter />
//   </React.StrictMode>
// )
